import { LocalizationProvider } from '@mui/x-date-pickers-pro';
import { AdapterMoment } from '@mui/x-date-pickers-pro/AdapterMoment';
import { deDE, enUS, esES, frFR, itIT, nbNO, svSE } from '@mui/x-date-pickers-pro/locales';
import React, { memo, useContext, useEffect, useState } from 'react';
import { IntlProvider, useIntl } from 'react-intl';
import { Navigate, Outlet, useLocation, useParams } from 'react-router';

import config from '../config';
import { ContextProvider } from '../ContextProvider';
import MapProvider from '../components/SearchMap/MapProvider';
import { getClientData } from '../util/clientData';
import { isCordova } from '../util/mobile/deviceDetection';

const muiLocaleMap = {
  de: deDE.components.MuiLocalizationProvider.defaultProps.localeText,
  en: enUS.components.MuiLocalizationProvider.defaultProps.localeText,
  es: esES.components.MuiLocalizationProvider.defaultProps.localeText,
  fr: frFR.components.MuiLocalizationProvider.defaultProps.localeText,
  it: itIT.components.MuiLocalizationProvider.defaultProps.localeText,
  no: nbNO.components.MuiLocalizationProvider.defaultProps.localeText,
  sv: svSE.components.MuiLocalizationProvider.defaultProps.localeText,
};

async function loadMessages(locale) {
  switch (locale) {
    case 'de':
      return import(/* webpackChunkName: "messages_de" */'../translations/de.json');
    case 'en':
      return import(/* webpackChunkName: "messages_en" */'../translations/en.json');
    case 'es':
      return import(/* webpackChunkName: "messages_es" */'../translations/es.json');
    case 'fr':
      return import(/* webpackChunkName: "messages_fr" */'../translations/fr.json');
    case 'it':
      return import(/* webpackChunkName: "messages_it" */'../translations/it.json');
    case 'no':
      return import(/* webpackChunkName: "messages_no" */'../translations/no.json');
    case 'sv':
      return import(/* webpackChunkName: "messages_sv" */'../translations/sv.json');
    default:
      return import(/* webpackChunkName: "messages_en" */'../translations/en.json');
  }
}

const MuiIntlProviderWrapperComponent = ({ children }) => {
  const intl = useIntl();
  const localeKey = intl.locale?.split('-')[0] || 'en';
  const localeText = muiLocaleMap[localeKey] || muiLocaleMap.en;

  return (
    <LocalizationProvider
      adapterLocale={intl.locale}
      dateAdapter={AdapterMoment}
      localeText={localeText}
    >
      {children}
    </LocalizationProvider>
  );
};

MuiIntlProviderWrapperComponent.displayName = 'MuiIntlProviderWrapper';

const MuiIntlProviderWrapper = memo(MuiIntlProviderWrapperComponent);

const IntlProviderWrapperComponent = ({ children, selectedLocale, serverMessages = null }) => {
  const getLocale = (locale) => locale && locale.split('-')[0];
  const getSupportedLocaleJson = (locale) =>
    config.languages[getLocale(locale)].replace('.json', '');

  const supportedLocale = getSupportedLocaleJson(selectedLocale);

  const [messages, setMessages] = useState(serverMessages);
  const [loadedLocale, setLoadedLocale] = useState(serverMessages ? supportedLocale : null);

  useEffect(() => {
    if (loadedLocale !== supportedLocale) {
      let cancelled = false;
      (async () => {
        try {
          const msgs = await loadMessages(supportedLocale);
          if (!cancelled) {
            setMessages(msgs.default || msgs);
            setLoadedLocale(supportedLocale);
            // Force browser to recalculate scroll area after locale content changes.
            // Without this, the viewport retains the scroll extent of the previous
            // (taller) page, leaving whitespace below shorter content.
            requestAnimationFrame(() => {
              document.documentElement.style.overflow = 'hidden';
              void document.documentElement.offsetHeight;
              document.documentElement.style.overflow = '';
              window.scrollTo(0, 0);
            });
          }
        } catch (e) {
          // Chunk load failure — keep showing current messages rather than crashing
          if (!cancelled) {
            console.error('Failed to load messages for locale:', supportedLocale, e);
          }
        }
      })();
      return () => { cancelled = true; };
    }
  }, [supportedLocale, loadedLocale]);

  if (messages === null) return null;

  // Use loadedLocale for IntlProvider to keep locale and messages in sync.
  // While new messages are loading, render with the previous locale's messages
  // to avoid format errors from mismatched variable names across translations.
  const activeLocale = loadedLocale || supportedLocale;

  return (
    <IntlProvider
      locale={activeLocale}
      messages={messages}>
      <MuiIntlProviderWrapper>
        {children}
      </MuiIntlProviderWrapper>
    </IntlProvider>
  );
};

IntlProviderWrapperComponent.displayName = 'IntlProviderWrapper';

const IntlProviderWrapper = memo(IntlProviderWrapperComponent);

const LocaleLayoutComponent = () => {
  const { locale: pathLocale } = useParams();
  const location = useLocation();
  const context = useContext(ContextProvider);
  const { pathname, search } = location;

  const getLocale = (locale) => locale && locale.split('-')[0];
  const isSupported = (locale) => !!config.languages[getLocale(locale)];

  const getCordovaLocale = () => isCordova() && getClientData('vansite.locale');

  if (getCordovaLocale() && getCordovaLocale() !== pathLocale) {
    return <Navigate replace to={`/${getCordovaLocale()}${pathname}${search}`} />;
  }

  const browserLocale =
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    window.navigator.language &&
    getLocale(navigator.language);

  const contextLocale =
    context &&
    context.locale &&
    (Array.isArray(context.locale)
      ? getLocale(context.locale[0])
      : getLocale(context.locale));
  if (context && context.locale && process.env.NODE_ENV === 'development') {
    console.log('context.locale', context.locale);
  }

  // Unsupported / missing-prefix locales are redirected by the `:locale` route
  // loader (createLocaleGuardLoader in dataRouter.js) so the redirect happens
  // server-side as a real 302 instead of rendering <Navigate> on the initial
  // StaticRouter pass. By the time this component renders, pathLocale is valid.

  const selectedLocale =
    (isSupported(pathLocale) && pathLocale) ||
    (isSupported(browserLocale) && browserLocale) ||
    (isSupported(contextLocale) && contextLocale) ||
    config.fallbackLocale;

  return (
    <IntlProviderWrapper selectedLocale={selectedLocale} serverMessages={context?.messages}>
      <MapProvider>
        <Outlet />
      </MapProvider>
    </IntlProviderWrapper>
  );
};

LocaleLayoutComponent.displayName = 'LocaleLayout';

const LocaleLayout = memo(LocaleLayoutComponent);

export default LocaleLayout;
